/**
 * Shell de la aplicación: sesión → dashboard. Refresca los datos cada 60 s (ETag/304) y, si los
 * datos tienen más de 15 s (el servidor refresca en segundo plano), vuelve a consultar a los 5 s.
 */
import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import type { Snapshot } from '@redmil/shared';
import { ApiError, getSnapshot, getStatus, logout, type CurrentUser } from './api';
import { Icon } from './components/icons';
import { isDemoRequested, loadDemoSnapshot } from './demo-mode';
import { ago } from './format';
import { Dashboard } from './views/dashboard';
import { Login } from './views/login';

type Phase = 'checking' | 'login' | 'ready';

const phase = signal<Phase>('checking');
const user = signal<CurrentUser | null>(null);
const adminConfigured = signal(true);
const snapshot = signal<Snapshot | null>(null);
const dataTime = signal(0);
const clockSkew = signal(0);
const now = signal(Date.now());
const error = signal<string | null>(null);
const offline = signal(false);

let timer: number | undefined;

async function refresh() {
  window.clearTimeout(timer);
  try {
    const r = await getSnapshot();
    snapshot.value = r.snapshot;
    dataTime.value = r.dataTime;
    clockSkew.value = Date.now() - r.serverTime;
    error.value = null;
    offline.value = false;
    const age = r.serverTime - r.dataTime;
    timer = window.setTimeout(refresh, age > 15_000 ? 5_000 : 60_000);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      user.value = null;
      phase.value = 'login';
      return;
    }
    offline.value = !(err instanceof ApiError);
    error.value = err instanceof ApiError ? err.message : null;
    timer = window.setTimeout(refresh, 30_000);
  }
}

async function start() {
  try {
    const s = await getStatus();
    adminConfigured.value = s.adminConfigured !== false;
    if (s.authenticated && s.user) {
      user.value = s.user;
      phase.value = 'ready';
      await refresh();
    } else {
      phase.value = 'login';
    }
  } catch {
    error.value = 'No se pudo conectar con el servidor.';
    phase.value = 'login';
  }
}

export function App() {
  const demo = isDemoRequested();

  useEffect(() => {
    const tick = window.setInterval(() => (now.value = Date.now()), 1000);
    if (demo) {
      user.value = { username: 'demo', name: 'Modo demo', roles: ['admin'], roleLabels: ['Demostración'], capabilities: ['viewCosts'], writeSheets: [] };
      loadDemoSnapshot().then((s) => {
        snapshot.value = s;
        dataTime.value = Date.now();
        phase.value = 'ready';
      });
    } else {
      void start();
    }
    return () => window.clearInterval(tick);
  }, [demo]);

  if (phase.value === 'checking') return <p class="loading">Cargando…</p>;
  if (phase.value === 'login')
    return (
      <Login
        adminConfigured={adminConfigured.value}
        onLogin={(u) => {
          user.value = u;
          phase.value = 'ready';
          void refresh();
        }}
      />
    );

  const s = snapshot.value;
  const ageMs = now.value - clockSkew.value - dataTime.value;
  return (
    <div class="shell">
      <header class="topbar">
        <div class="brand">
          <span class="brand-mark" aria-hidden="true">
            <Icon name="board" size={18} />
          </span>
          <span>Taller Eléctrico</span>
        </div>
        <div class="topbar-right">
          {demo && <span class="badge badge-demo">MODO DEMO</span>}
          {s && !demo && (
            <span class={`badge ${s.stale || ageMs > 120_000 ? 'badge-warn' : 'badge-soft'}`} title="Antigüedad de los datos del libro">
              Datos {ago(ageMs)}
            </span>
          )}
          <span class="user" title={user.value?.roleLabels.join(', ')}>
            <Icon name="user" size={18} /> {user.value?.name}
          </span>
          {!demo && (
            <button
              class="btn btn-ghost"
              onClick={async () => {
                await logout().catch(() => {});
                user.value = null;
                snapshot.value = null;
                phase.value = 'login';
              }}
            >
              <Icon name="logout" size={18} /> Salir
            </button>
          )}
        </div>
      </header>
      <main class="content">
        {offline.value && (
          <p class="notice" role="status">
            Sin conexión: mostrando datos {ago(ageMs)}.
          </p>
        )}
        {error.value && (
          <p class="notice notice-error" role="alert">
            {error.value}
          </p>
        )}
        {s?.stale && <p class="notice">Google no respondió; se muestran los últimos datos disponibles ({ago(ageMs)}).</p>}
        {!s && !error.value && <p class="loading">Leyendo el libro de Google Sheets…</p>}
        {s && <Dashboard snapshot={s} />}
      </main>
    </div>
  );
}
